import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { SharedModule } from '../../../shared/shared.module';
import { DashboardService } from '../../../services/dashboard.service';
import { CountUp } from 'countup.js';
import { Chart, registerables } from 'chart.js';
import { AdministratorService } from '../../../services/administrator.service';

Chart.register(...registerables);

@Component({
  selector: 'app-salesdashboard',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './salesdashboard.component.html',
  styleUrl: './salesdashboard.component.scss',
  providers: [DatePipe]
})
export class SalesdashboardComponent implements OnInit, OnDestroy {

  loader = true;
  todayDate = '';

  data: any = null;

  totalRevenue = '₹0';
  avgOrderValue = '₹0';
  totalGST = '₹0';
  totalOrders = 0;
  totalCustomers = 0;
  invoicedOrders = 0;
  pendingOrders = 0;
  revenueGrowth = 0;
  totalProductsSold = 0;

  trendChartData: any = { labels: [], datasets: [] };
  trendChartOptions: any = {};
  supplyChartData: any = { labels: [], datasets: [] };
  supplyChartOptions: any = {};
  supplyBarChartData: any = { labels: [], datasets: [] };
  supplyBarChartOptions: any = {};

  topProducts: any[] = [];
  recentOrdersList: any[] = [];
  gstInfo: any = {};

  warehouseList: any[] = [];
  selectedWarehouse: any;

  prodColors = ['#346d7e', '#0369a1', '#0d9488', '#e11d48', '#d97706', '#16a34a'];
  avatarColors = ['#909940', '#0369a1', '#0d9488', '#e11d48', '#d97706', '#16a34a'];

  private statusColorMap: Record<string, string> = {
    'Invoiced': '#16a34a',
    'Order': '#0369a1',
    'Draft': '#d97706',
    'Other': '#6b7280'
  };

  selectedPeriod: string = 'TM';

  periodOptions = [
    { label: 'This Month', value: 'TM' },
    { label: '6 Months', value: '6M' },
    { label: '12 Months', value: '12M' },
    { label: 'Last Year', value: 'LY' }
  ];

  constructor(
    private dashboardService: DashboardService,
    private datePipe: DatePipe,
    private administratorService: AdministratorService
  ) { }

  ngOnInit(): void {
    document.title = 'Sales Dashboard';
    this.todayDate = this.datePipe.transform(new Date(), 'dd MMM yyyy') ?? '';
    this.initChartOptions();
    this.loadData();
    this.getWarehouse();
  }

  ngOnDestroy(): void { }

  getStatusColor(status: string): string {
    return this.statusColorMap[status] ?? '#6b7280';
  }

  refreshData(): void {
    this.loadData();
  }

  onPeriodChange(): void {
    this.loadData();
  }

  loadData(): void {
    this.loader = true;
    const warehouseId = this.selectedWarehouse?.warehouseId || 0;
    this.dashboardService.getSalesDashboardData(this.selectedPeriod, warehouseId).subscribe({
      next: (res: any) => {
        this.data = res;
        this.processData();
        this.loader = false;
        setTimeout(() => this.runCountUps(), 120);
      },
      error: (err: any) => {
        console.error('Dashboard load failed', err);
        this.loader = false;
      }
    });
  }

  processData(): void {
    if (!this.data) return;

    const km = this.data.keyMetrics;
    if (km) {
      this.totalRevenue = this.formatINR(km.totalRevenue, 0);
      this.avgOrderValue = this.formatINR(km.averageOrderValue, 0);
      this.totalOrders = km.totalOrders;
      this.totalCustomers = km.totalCustomers;
      this.invoicedOrders = km.invoicedOrders;
      this.pendingOrders = km.pendingOrders;
      this.totalProductsSold = km.totalProductsSold ?? 0;

      const prev = km.lastMonthRevenue || 0;
      const curr = km.currentMonthRevenue || 0;
      this.revenueGrowth = prev > 0 ? +(((curr - prev) / prev) * 100).toFixed(1) : 0;
    }

    if (this.data.topProducts?.length) {
      const maxRev = this.data.topProducts[0].totalRevenue || 1;
      this.topProducts = this.data.topProducts.map((p: any) => ({
        name: p.productDescription,
        revenue: this.formatINR(p.totalRevenue, 0),
        qty: p.totalQuantitySold,
        pct: Math.round((p.totalRevenue / maxRev) * 100)
      }));
    }

    if (this.data.recentOrders?.length) {
      this.recentOrdersList = this.data.recentOrders
        .filter((o: any) =>
          !o.salesOrderStatus?.includes('TEMP') &&
          !o.salesOrderStatus?.includes('DRAFT')
        )
        .map((o: any) => {
          let st = o.salesOrderStatus;
          let cl = 'draft';

          if (st === 'INVOICE') { st = 'Invoiced'; cl = 'invoice'; }
          else if (st === 'ORDER') { st = 'Order'; cl = 'open'; }

          return {
            salesOrderNumber: o.salesOrderNumber,
            date: this.datePipe.transform(o.salesOrderDate, 'dd/MM/yy') ?? '-',
            customerName: o.customerName,
            initials: o.customerName ? o.customerName.substring(0, 2).toUpperCase() : 'NA',
            total: this.formatINR(o.orderTotal, 0),
            status: st,
            statusClass: cl
          };
        });
    }

    if (this.data.salesStatus) {
      this.data.salesStatus = this.data.salesStatus
        .filter((s: any) =>
          !s.status?.includes('TEMP') &&
          !s.status?.includes('DRAFT')
        )
        .map((s: any) => ({
          ...s,
          formattedRevenue: this.formatINR(s.totalRevenue, 0)
        }));
    }

    if (this.data.supplyTypeDistribution) {
      this.data.supplyTypeDistribution = this.data.supplyTypeDistribution.map((s: any) => ({
        ...s,
        formattedRevenue: this.formatINR(s.totalRevenue, 0)
      }));
    }

    this.gstInfo = this.data.gstBreakdown || {};

    this.buildTrendChart();
    this.buildSupplyDoughnutChart();
    this.buildSupplyBarChart();
  }

  buildTrendChart(): void {
    const trend = this.data.monthlyTrend || [];
    const labels = trend.map((m: any) => m.monthYear);
    const values = trend.map((m: any) => m.revenue);

    const gradientFn = (ctx: any) => {
      const chart = ctx.chart;
      const { ctx: c, chartArea } = chart;
      if (!chartArea) return 'rgba(124,58,237,.3)';
      const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      g.addColorStop(0, 'rgba(58, 237, 58, 0.45)');
      g.addColorStop(0.6, 'rgba(14,165,233,.2)');
      g.addColorStop(1, 'rgba(124,58,237,.02)');
      return g;
    };

    this.trendChartData = {
      labels,
      datasets: [
        {
          type: 'line',
          label: 'Revenue',
          data: values,
          borderColor: '#3aedc0',
          borderWidth: 2.5,
          pointRadius: 5,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: '#ce1ee6',
          pointBorderWidth: 2.5,
          tension: 0.42,
          fill: true,
          backgroundColor: gradientFn,
          order: 1
        },
        {
          type: 'bar',
          label: 'Orders',
          data: trend.map((m: any) => m.orderCount),
          backgroundColor: 'rgba(14,165,233,.18)',
          borderRadius: 6,
          barThickness: 22,
          yAxisID: 'y1',
          order: 2
        }
      ]
    };
  }

  buildSupplyDoughnutChart(): void {
    const supply = this.data.supplyTypeDistribution || [];
    const colors = ['#2ac6d1', '#0369a1', '#0d9488', '#d97706'];

    this.supplyChartData = {
      labels: supply.map((s: any) => s.supplyType),
      datasets: [{
        data: supply.map((s: any) => s.totalRevenue),
        backgroundColor: colors,
        borderWidth: 3,
        borderColor: '#ffffff',
        hoverOffset: 6
      }]
    };
  }

  buildSupplyBarChart(): void {
    const supply = this.data.supplyTypeDistribution || [];
    const colors = ['#c52d80', '#0369a1', '#0d9488', '#d97706'];

    this.supplyBarChartData = {
      labels: supply.map((s: any) => s.supplyType),
      datasets: [{
        label: 'Revenue',
        data: supply.map((s: any) => s.totalRevenue),
        backgroundColor: colors.map(c => c + 'cc'),
        borderRadius: 4,
        borderSkipped: false,
        barThickness: 15
      }]
    };
  }

  initChartOptions(): void {

    this.trendChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(13,15,26,.85)',
          padding: 12,
          titleFont: { family: 'Syne', weight: '700', size: 13 },
          bodyFont: { family: 'DM Sans', size: 12 },
          callbacks: {
            label: (ctx: any) => {
              if (ctx.dataset.type === 'bar') return `  Orders: ${ctx.raw}`;
              return `  Revenue: ₹${ctx.raw.toLocaleString('en-IN')}`;
            }
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { family: 'DM Sans', size: 11 }, color: '#9ca3af' }
        },
        y: {
          grid: { color: 'rgba(0,0,0,.04)', drawBorder: false },
          ticks: {
            font: { family: 'DM Sans', size: 10 },
            color: '#9ca3af',
            callback: (v: any) => {
              if (v >= 1_00_00_000) return '₹' + (v / 1_00_00_000).toFixed(1) + 'Cr';
              if (v >= 1_00_000) return '₹' + (v / 1_00_000).toFixed(1) + 'L';
              if (v >= 1_000) return '₹' + (v / 1_000).toFixed(0) + 'K';
              return '₹' + v;
            }
          }
        },
        y1: {
          position: 'right',
          grid: { display: false },
          ticks: {
            font: { family: 'DM Sans', size: 10 },
            color: '#0ea5e9',
            callback: (v: any) => v + ' ord'
          }
        }
      }
    };

    this.supplyChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '62%',
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) => ` ₹${ctx.raw.toLocaleString('en-IN')}`
          }
        }
      }
    };

    this.supplyBarChartOptions = {
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: any) => ` ₹${ctx.raw.toLocaleString('en-IN')}`
          }
        }
      },
      scales: {
        x: {
          grid: { color: 'rgba(0,0,0,.04)', drawBorder: false },
          ticks: {
            font: { family: 'DM Sans', size: 10 },
            color: '#9ca3af',
            callback: (v: any) => {
              if (v >= 1_00_00_000) return '₹' + (v / 1_00_00_000).toFixed(1) + 'Cr';
              if (v >= 1_00_000) return '₹' + (v / 1_00_000).toFixed(1) + 'L';
              return '₹' + v;
            }
          }
        },
        y: {
          grid: { display: false },
          ticks: { font: { family: 'DM Sans', size: 11 }, color: '#6b7280' }
        }
      }
    };
  }

  runCountUps(): void {
    const km = this.data?.keyMetrics;
    if (!km) return;

    const opts = (prefix = '', decimals = 0) => ({
      decimalPlaces: decimals,
      separator: ',',
      duration: 4,
      useEasing: true,
      useGrouping: true,
      prefix: prefix,
      formattingFn: (num: number) => {
        return prefix + num.toLocaleString('en-IN', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        });
      }
    });

    new CountUp('kRev', km.totalRevenue, opts('₹', 0)).start();
    new CountUp('kOrd', km.totalOrders, opts('', 0)).start();
    new CountUp('kInv', km.invoicedOrders, opts('', 0)).start();
    new CountUp('kCus', km.totalCustomers, opts('', 0)).start();
  }

  formatINR(value: number, digits: number = 0): string {
    if (!value) return '₹0';
    return '₹' + value.toLocaleString('en-IN', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    });
  }

  getWarehouse() {
    const pageRequest = {
      pageIndex: 0,
      pageSize: 100,
      id: 0
    };

    this.administratorService.getAllWarehouse(pageRequest).subscribe({
      next: (res: any) => {
        this.warehouseList = res.warehouses || [];

        if (this.warehouseList.length > 0) {
          this.selectedWarehouse = this.warehouseList[0];
          this.loadData();
        }
      },
      error: err => console.error(err)
    });
  }

  onWarehouseChange(event: any) {
    if (event?.value) {
      this.selectedWarehouse = event.value;
      this.loadData();
    }
  }
}